import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AREA_DESCRIPTIONS,
  AREA_LABELS,
  AREAS,
  describeEffective,
  draftEffective,
  WILDCARD,
  type Area,
  type AreaSetting,
  type GrantDraft,
  type Level,
  type WildcardSetting,
} from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { IconAlertTriangle } from "@tabler/icons-react";

const AREA_OPTIONS: { value: AreaSetting; label: string }[] = [
  { value: "unset", label: "Not set" },
  { value: "none", label: "None" },
  { value: "read", label: "Read" },
  { value: "write", label: "Write" },
];

const WILDCARD_OPTIONS: { value: WildcardSetting; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "read", label: "Read" },
  { value: "write", label: "Write" },
];

function LevelPill({ level }: { level: Level }) {
  return (
    <span
      className={cn(
        "rounded-full px-1.5 py-px text-[10px] font-medium uppercase tracking-wide",
        level === "write" && "bg-primary/10 text-primary",
        level === "read" && "bg-muted text-muted-foreground",
        level === "none" && "bg-transparent text-muted-foreground/50",
      )}
    >
      {level}
    </span>
  );
}

function Row<T extends string>({
  label,
  hint,
  options,
  value,
  onChange,
  effective,
  className,
}: {
  label: React.ReactNode;
  hint: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  effective?: Level;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4",
        className,
      )}
    >
      <div className="min-w-0 space-y-0.5">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <span className="min-w-0 truncate">{label}</span>
          {effective !== undefined && <LevelPill level={effective} />}
        </div>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      <ToggleGroup
        type="single"
        variant="outline"
        size="sm"
        value={value}
        // Radix clears the value when the active item is pressed again.
        onValueChange={(next) => next && onChange(next as T)}
        className="w-full shrink-0 sm:w-auto"
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value}
            className="h-9 flex-1 text-xs sm:flex-none"
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

export function PermissionsEditor({
  draft,
  onChange,
  unknownEntries,
}: {
  draft: GrantDraft;
  onChange: (draft: GrantDraft) => void;
  unknownEntries: string[];
}) {
  const effective = draftEffective(draft);
  const grantsSpecialUsers = effective["admin/special-users"] === "write";

  const setArea = (area: Area, setting: AreaSetting) =>
    onChange({ ...draft, areas: { ...draft.areas, [area]: setting } });

  return (
    <div className="space-y-3">
      {unknownEntries.length > 0 && (
        <div className="flex gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          <IconAlertTriangle className="size-4 shrink-0" />
          <div className="min-w-0 space-y-1">
            <p className="font-medium">
              This grant holds {unknownEntries.length} unrecognized{" "}
              {unknownEntries.length === 1 ? "entry" : "entries"}, so the user
              currently has no admin access at all.
            </p>
            <p className="break-all font-mono opacity-80">
              {unknownEntries.join(", ")}
            </p>
            <p>Saving removes {unknownEntries.length === 1 ? "it" : "them"}.</p>
          </div>
        </div>
      )}

      <div className="rounded-lg border">
        <div className="border-b bg-muted/40 px-3">
          <Row
            label={<span className="font-mono text-xs">{WILDCARD}</span>}
            hint="Every admin area without its own setting. Never covers docs."
            options={WILDCARD_OPTIONS}
            value={draft.wildcard}
            onChange={(value) => onChange({ ...draft, wildcard: value })}
          />
        </div>

        <div className="divide-y px-3">
          {AREAS.map((area) => (
            <Row
              key={area}
              label={AREA_LABELS[area]}
              hint={AREA_DESCRIPTIONS[area]}
              options={AREA_OPTIONS}
              value={draft.areas[area]}
              onChange={(value) => setArea(area, value)}
              effective={effective[area]}
            />
          ))}
        </div>
      </div>

      <div className="rounded-lg border bg-muted/40 p-3 text-xs">
        <p className="font-medium text-muted-foreground">Effective access</p>
        <p className="mt-1">{describeEffective(effective)}</p>
      </div>

      {grantsSpecialUsers && (
        <div className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
          <IconAlertTriangle className="size-4 shrink-0" />
          <span>
            Write on Special Users is effectively full admin — it can grant any
            permission to anyone, including itself.
          </span>
        </div>
      )}
    </div>
  );
}
